import { color } from 'console-log-colors'
import type { PartialPharmacyProductParams } from '~/models/middleware-types'

/**
 * Ensures that the pharmacy and product params are valid
 * As long as the pharmacy id is valid, slug and name will be fixed if they are not
 * Will be navigated to correct pharmacy product url
 */
export default defineNuxtRouteMiddleware(async (to) => {
  onShowLogs(() => console.info(color.blueBright('[02] product-routing\n'), to.path))

  const { validateIncomingProduct } = useRoutingValidator()
  const router = useRouter()

  const {
    pharmacyName,
    pharmacyPlz,
    pharmacyId,
    productName,
    pzn,
  } = to.params as PartialPharmacyProductParams

  // pharmacyName is sometimes an array
  const productParams = {
    pharmacyName: Array.isArray(pharmacyName) ? pharmacyName.at(0) : pharmacyName,
    pharmacyPlz,
    pharmacyId,
    productName,
    pzn,
  }

  // [pharmacyId]/produkt/[productName]/[pzn] -> [pharmacyName]-[pharmacyPlz]-[pharmacyId]/produkt/[productName]/[pzn]
  // `short pharmacy url => full pharmacy url` for product page is handled within validator because we already fetch and validate pharmacy url there
  // but for other pharmacy-related pages with short url, they are handled in handleShortPharmacyPages
  const productValidated = await validateIncomingProduct(productParams)

  // Fixes pharmacyName expected type when passing in explicit route name
  const productValidatedParams = ensurePharmacyParamsType(productValidated.params!)

  switch (productValidated.status) {
    case 200:
      if (isClient) {
        proceedToPharmacySwitchCheck(productParams.pharmacyId)
      }
      break
    case 301: {
      const correctPharmacyRoute = router.resolve({
        name: productValidated.name,
        params: productValidatedParams,
        query: to.query,
        hash: to.hash,
      })
      return customNavigateTo(correctPharmacyRoute, { redirectCode: 301 })
    }
    case 302: {
      const correctPharmacyRoute = router.resolve({
        name: productValidated.name!,
        query: to.params,
        hash: to.hash,
      })
      return customNavigateTo(correctPharmacyRoute, { redirectCode: 302 })
    }
    default:
      return abortNavigation({ statusCode: productValidated.status })
  }
})
